// eslint-disable-next-line simple-import-sort/imports
import { UserProfile } from '@/types/user.type'
import * as React from 'react'
import { PinnedMessage } from '../../../types/chat.type'

interface ChatState {
  isOpen: boolean
  isMaximized: boolean
  newMessage: string
  selectedConversationId: number | undefined
  showNewConversationDialog: boolean
  showMessageSearch: boolean
  showUserSearch: boolean
  showSearchDialog: boolean
  searchQuery: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filteredConversations: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filteredMessages: any[]
  searchResults: {
    users: Array<{
      id: number
      firstName: string
      lastName: string
      email?: string
      avatar?: string
      profileId?: string
    }>
    messages: Array<{
      id: number
      content: string
      sender: { id: number; firstName: string; lastName: string; avatar?: string }
      createdAt: string
      conversationId: number
    }>
    conversations: Array<{
      id: number
      name: string
      description?: string
      avatar?: string
      type: string
      lastMessageAt?: string
      lastMessageContent?: string
      participants: Array<{
        id: number
        firstName: string
        lastName: string
        email?: string
        avatar?: string
        profileId?: string
      }>
    }>
  }
  editingMessageId: number | null
  editingConversationId: number | null
  editingConversationName: string
  showEditConversationDialog: boolean
  editMessageContent: string
  newConversationName: string
  selectedUsers: Array<{
    id: number
    firstName: string
    lastName: string
    email?: string
    avatar?: string
    profileId?: string
  }>
  pendingTargetUserId: number | undefined
  targetUserName: string
  targetUserAvatar: string
  hasCheckedExistingConversation: boolean
  jobName: string
  deletingMessageId: number | null
  showDeleteConfirmDialog: boolean
  isDeleting: boolean
  deletingConversationId: number | null
  showDeleteConversationDialog: boolean
  isDeletingConversation: boolean
  showCreateGroupChatDialog: boolean
  showPinnedMessagesDialog: boolean
  selectedFile: File | null
  isUploadingFile: boolean
  previousMessageCount: number
  isMobileView: boolean
  showMobileConversationList: boolean
  showConversationSettings: boolean
  isConversationRead: boolean
  // Reply state
  replyingToMessage: {
    id: number
    content: string
    sender: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
    }
    type: string
    fileUrl?: string
    fileName?: string
  } | null
  // Pinned messages state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pinnedMessages: PinnedMessage[]
  isPinnedMessagesExpanded: boolean
}

interface UseChatStateProps {
  initialConversationId?: number
  authUser: UserProfile | null
}

interface ChatStateReturn extends ChatState {
  setIsOpen: (isOpen: boolean) => void
  setIsMaximized: (isMaximized: boolean) => void
  setNewMessage: (message: string) => void
  setSelectedConversationId: (id: number | undefined) => void
  setShowNewConversationDialog: (show: boolean) => void
  setShowMessageSearch: (show: boolean) => void
  setShowUserSearch: (show: boolean) => void
  setShowSearchDialog: (show: boolean) => void
  setSearchQuery: (query: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilteredConversations: (conversations: any[]) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilteredMessages: (messages: any[]) => void
  setSearchResults: (results: ChatState['searchResults']) => void
  setEditingMessageId: (id: number | null) => void
  setEditingConversationId: (id: number | null) => void
  setEditingConversationName: (name: string) => void
  setShowEditConversationDialog: (show: boolean) => void
  setEditMessageContent: (content: string) => void
  setNewConversationName: (name: string) => void
  setSelectedUsers: (
    users: Array<{ id: number; firstName: string; lastName: string; email?: string; avatar?: string }>
  ) => void
  setPendingTargetUserId: (id: number | undefined) => void
  setTargetUserName: (name: string) => void
  setTargetUserAvatar: (avatar: string) => void
  setHasCheckedExistingConversation: (checked: boolean) => void
  setJobName: (name: string) => void
  setDeletingMessageId: (id: number | null) => void
  setShowDeleteConfirmDialog: (show: boolean) => void
  setIsDeleting: (deleting: boolean) => void
  setDeletingConversationId: (id: number | null) => void
  setShowDeleteConversationDialog: (show: boolean) => void
  setIsDeletingConversation: (deleting: boolean) => void
  setShowCreateGroupChatDialog: (show: boolean) => void
  setShowPinnedMessagesDialog: (show: boolean) => void
  setSelectedFile: (file: File | null) => void
  setIsUploadingFile: (uploading: boolean) => void
  setPreviousMessageCount: (count: number) => void
  setIsMobileView: (isMobile: boolean) => void
  setShowMobileConversationList: (show: boolean) => void
  setShowConversationSettings: (show: boolean) => void
  setIsConversationRead: (isRead: boolean) => void
  setReplyingToMessage: (message: ChatState['replyingToMessage']) => void
  setPinnedMessages: (messages: PinnedMessage[]) => void
  setIsPinnedMessagesExpanded: (expanded: boolean) => void
}

export const useChatState = ({ initialConversationId, authUser: _authUser }: UseChatStateProps): ChatStateReturn => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [isMaximized, setIsMaximized] = React.useState(false)
  const [newMessage, setNewMessage] = React.useState('')
  const [selectedConversationId, setSelectedConversationId] = React.useState<number | undefined>(initialConversationId)
  const [showNewConversationDialog, setShowNewConversationDialog] = React.useState(false)
  const [showMessageSearch, setShowMessageSearch] = React.useState(false)
  const [showUserSearch, setShowUserSearch] = React.useState(false)
  const [showSearchDialog, setShowSearchDialog] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filteredConversations, setFilteredConversations] = React.useState<any[]>([])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filteredMessages, setFilteredMessages] = React.useState<any[]>([])
  const [searchResults, setSearchResults] = React.useState<ChatState['searchResults']>({
    users: [],
    messages: [],
    conversations: [],
  })
  const [editingMessageId, setEditingMessageId] = React.useState<number | null>(null)
  const [editingConversationId, setEditingConversationId] = React.useState<number | null>(null)
  const [editingConversationName, setEditingConversationName] = React.useState('')
  const [showEditConversationDialog, setShowEditConversationDialog] = React.useState(false)
  const [editMessageContent, setEditMessageContent] = React.useState('')
  const [newConversationName, setNewConversationName] = React.useState('')
  const [selectedUsers, setSelectedUsers] = React.useState<
    Array<{ id: number; firstName: string; lastName: string; email?: string; avatar?: string; profileId?: string }>
  >([])
  const [pendingTargetUserId, setPendingTargetUserId] = React.useState<number | undefined>(undefined)
  const [targetUserName, setTargetUserName] = React.useState<string>('')
  const [targetUserAvatar, setTargetUserAvatar] = React.useState<string>('')
  const [hasCheckedExistingConversation, setHasCheckedExistingConversation] = React.useState(false)
  const [jobName, setJobName] = React.useState<string>('')
  const [deletingMessageId, setDeletingMessageId] = React.useState<number | null>(null)
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [deletingConversationId, setDeletingConversationId] = React.useState<number | null>(null)
  const [showDeleteConversationDialog, setShowDeleteConversationDialog] = React.useState(false)
  const [isDeletingConversation, setIsDeletingConversation] = React.useState(false)
  const [showCreateGroupChatDialog, setShowCreateGroupChatDialog] = React.useState(false)
  const [showPinnedMessagesDialog, setShowPinnedMessagesDialog] = React.useState(false)
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [isUploadingFile, setIsUploadingFile] = React.useState(false)
  const [previousMessageCount, setPreviousMessageCount] = React.useState(0)
  const [isMobileView, setIsMobileView] = React.useState(false)
  const [showMobileConversationList, setShowMobileConversationList] = React.useState(true)
  const [showConversationSettings, setShowConversationSettings] = React.useState(false)
  const [isConversationRead, setIsConversationRead] = React.useState(false)
  const [replyingToMessage, setReplyingToMessage] = React.useState<ChatState['replyingToMessage']>(null)
  const [pinnedMessages, setPinnedMessages] = React.useState<ChatState['pinnedMessages']>([])
  const [isPinnedMessagesExpanded, setIsPinnedMessagesExpanded] = React.useState(false)

  return {
    // State
    isOpen,
    isMaximized,
    newMessage,
    selectedConversationId,
    showNewConversationDialog,
    showMessageSearch,
    showUserSearch,
    showSearchDialog,
    searchQuery,
    filteredConversations,
    filteredMessages,
    searchResults,
    editingMessageId,
    editingConversationId,
    editingConversationName,
    showEditConversationDialog,
    editMessageContent,
    newConversationName,
    selectedUsers,
    pendingTargetUserId,
    targetUserName,
    targetUserAvatar,
    hasCheckedExistingConversation,
    jobName,
    deletingMessageId,
    showDeleteConfirmDialog,
    isDeleting,
    deletingConversationId,
    showDeleteConversationDialog,
    isDeletingConversation,
    showCreateGroupChatDialog,
    showPinnedMessagesDialog,
    selectedFile,
    isUploadingFile,
    previousMessageCount,
    isMobileView,
    showMobileConversationList,
    showConversationSettings,
    isConversationRead,
    replyingToMessage,
    pinnedMessages,
    isPinnedMessagesExpanded,

    // Setters
    setIsOpen,
    setIsMaximized,
    setNewMessage,
    setSelectedConversationId,
    setShowNewConversationDialog,
    setShowMessageSearch,
    setShowUserSearch,
    setShowSearchDialog,
    setSearchQuery,
    setFilteredConversations,
    setFilteredMessages,
    setSearchResults,
    setEditingMessageId,
    setEditingConversationId,
    setEditingConversationName,
    setShowEditConversationDialog,
    setEditMessageContent,
    setNewConversationName,
    setSelectedUsers,
    setPendingTargetUserId,
    setTargetUserName,
    setTargetUserAvatar,
    setHasCheckedExistingConversation,
    setJobName,
    setDeletingMessageId,
    setShowDeleteConfirmDialog,
    setIsDeleting,
    setDeletingConversationId,
    setShowDeleteConversationDialog,
    setIsDeletingConversation,
    setShowCreateGroupChatDialog,
    setShowPinnedMessagesDialog,
    setSelectedFile,
    setIsUploadingFile,
    setPreviousMessageCount,
    setIsMobileView,
    setShowMobileConversationList,
    setShowConversationSettings,
    setIsConversationRead,
    setReplyingToMessage,
    setPinnedMessages,
    setIsPinnedMessagesExpanded,
  }
}
